import { Button, Heading, Input, Text } from '@youversion/design-system-components';
import { Form, Formik } from 'formik';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';

interface Props {
  handleSubmit: (values: FormValues) => void;
}

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
};

export function ConfirmAppleAccount({ handleSubmit }: Props) {
  const { t } = useTranslation(['common', 'createAccount']);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('createAccount:errors.invalid_email'))
      .required(t('createAccount:errors.email_required')),
    firstName: Yup.string().max(32, t('name_max_length')).required(t('createAccount:errors.first_name_required')),
    lastName: Yup.string().max(32, t('name_max_length')).required(t('createAccount:errors.last_name_required')),
  });

  function isSubmittable(values: FormValues) {
    return values.firstName && values.lastName && values.email;
  }

  return (
    <Formik
      initialValues={{ email: '', firstName: '', lastName: '' }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleBlur, handleChange, handleSubmit, values, errors, touched }) => (
        <Form className='flex flex-col gap-3 w-full mbs-5' onSubmit={handleSubmit}>
          <div className='flex flex-col space-y-2'>
            <Heading
              className='whitespace-nowrap text-text-light dark:text-text-dark'
              level={2}
              size='lg'
              variant='headline'
            >
              {t('common:complete_profile')}
            </Heading>
            <Input
              className='w-full text-text-light dark:text-text-dark'
              error={errors.firstName && touched.firstName ? errors.firstName : undefined}
              id='firstName'
              label={t('createAccount:first_name')}
              name='firstName'
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.firstName}
            />

            <Input
              className='w-full text-text-light dark:text-text-dark'
              error={errors.lastName && touched.lastName ? errors.lastName : undefined}
              id='lastName'
              label={t('createAccount:last_name')}
              name='lastName'
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.lastName}
            />
          </div>
          <Text className='text-text-light dark:text-text-dark' size='sm' variant='paragraph'>
            {t('common:email_recovery')}
          </Text>
          <Input
            className='text-text-light dark:text-text-dark'
            error={errors.email && touched.email ? errors.email : undefined}
            id='email'
            label={t('createAccount:email')}
            name='email'
            onBlur={handleBlur}
            onChange={handleChange}
            type='email'
            value={values.email}
          />
          <Button
            className='dark:disabled:bg-gray-30 dark:disabled:text-gray-10'
            disabled={!isSubmittable(values)}
            type='submit'
            variant='secondary'
          >
            {t('createAccount:create_account')}
          </Button>
        </Form>
      )}
    </Formik>
  );
}
